import { useCallback, useEffect, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { useIsMobile } from '@/components/ui/use-mobile';
import { csrfFetch } from '@/lib/csrf';
import { logError } from '@/lib/logger';
import { useEmoviDoc } from './hooks/useEmoviDoc';
import { EmoviDocChat } from './EmoviDocChat';
import { EmoviDocConversationList } from './EmoviDocConversationList';
import { EmoviDocDisclaimer } from './EmoviDocDisclaimer';
import { EmoviDocPanelHeader } from './EmoviDocPanelHeader';
import { GENERIC_ERROR_MESSAGE } from './constants';
import type { EmoviDocConversation } from './types';

type EmoviDocPanelProps = {
  patientId: number;
};

type ConversationsResponse = {
  conversations: EmoviDocConversation[];
};

export function EmoviDocPanel({ patientId }: EmoviDocPanelProps) {
  const { isOpen, setOpen, conversationId, setConversationId } = useEmoviDoc();
  const isMobile = useIsMobile();
  const [conversations, setConversations] = useState<EmoviDocConversation[]>([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [panelError, setPanelError] = useState<string | null>(null);
  const [isCreatingConversation, setIsCreatingConversation] = useState(false);
  const [isDeletingConversation, setIsDeletingConversation] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<number | null>(null);

  const selectedConversation =
    conversations.find((conversation) => conversation.id === conversationId) ?? null;

  const loadConversations = useCallback(async (signal?: AbortSignal) => {
    setLoadingConversations(true);
    setPanelError(null);

    try {
      const response = await csrfFetch(`/therapists/patients/${patientId}/emovi_doc`, {
        method: 'GET',
        signal,
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const payload = (await response.json()) as ConversationsResponse;
      setConversations(Array.isArray(payload.conversations) ? payload.conversations : []);
    } catch (error) {
      if ((error as Error)?.name === 'AbortError') return;
      logError('emovidoc.load_conversations.error', error);
      setPanelError('Impossibile caricare le conversazioni');
    } finally {
      setLoadingConversations(false);
    }
  }, [patientId]);

  const retryLoadConversations = useCallback(() => {
    void loadConversations();
  }, [loadConversations]);

  const createConversation = useCallback(async () => {
    setIsCreatingConversation(true);
    setPanelError(null);

    try {
      const response = await csrfFetch(`/therapists/patients/${patientId}/emovi_doc`, {
        method: 'POST',
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const payload = (await response.json()) as { conversation: EmoviDocConversation };
      if (!payload.conversation) throw new Error('Missing conversation payload');

      setConversations((prev) => [payload.conversation, ...prev]);
      setConversationId(payload.conversation.id);
    } catch (error) {
      logError('emovidoc.create_conversation.error', error);
      setPanelError(GENERIC_ERROR_MESSAGE);
    } finally {
      setIsCreatingConversation(false);
    }
  }, [patientId, setConversationId]);

  const deleteConversation = useCallback(
    async (targetConversationId: number) => {
      setIsDeletingConversation(targetConversationId);

      try {
        const response = await csrfFetch(
          `/therapists/patients/${patientId}/emovi_doc/${targetConversationId}`,
          { method: 'DELETE' },
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        setConversations((prev) =>
          prev.filter((conversation) => conversation.id !== targetConversationId),
        );
        if (conversationId === targetConversationId) {
          setConversationId(null);
        }
      } catch (error) {
        logError('emovidoc.delete_conversation.error', error);
        setPanelError(GENERIC_ERROR_MESSAGE);
      } finally {
        setIsDeletingConversation(null);
      }
    },
    [conversationId, patientId, setConversationId],
  );

  const openConversation = useCallback(
    (targetConversationId: number) => {
      setConversationId(targetConversationId);
    },
    [setConversationId],
  );

  const handleConversationUpdated = useCallback((conv: EmoviDocConversation) => {
    setConversations((prev) => prev.map((item) => (item.id === conv.id ? conv : item)));
  }, []);

  const handleBack = useCallback(() => {
    setConversationId(null);
  }, [setConversationId]);

  const onClosePanel = useCallback(
    (open: boolean) => {
      setOpen(open);
    },
    [setOpen],
  );

  useEffect(() => {
    if (!isOpen) return;
    const controller = new AbortController();
    void loadConversations(controller.signal);
    return () => controller.abort();
  }, [isOpen, loadConversations]);

  return (
    <Sheet open={isOpen} onOpenChange={onClosePanel}>
      <SheetContent
        side={isMobile ? 'bottom' : 'right'}
        className={
          isMobile
            ? 'h-[88svh] rounded-t-2xl p-0 gap-0 flex flex-col'
            : 'min-w-[32rem] max-w-[90vw] p-0 gap-0 flex flex-col'
        }
      >
        <EmoviDocPanelHeader />

        <div className="flex-1 min-h-0 relative">
          {!conversationId ? (
            <EmoviDocConversationList
              loading={loadingConversations}
              error={panelError}
              conversations={conversations}
              isCreatingConversation={isCreatingConversation}
              isDeletingConversation={isDeletingConversation}
              onRetry={retryLoadConversations}
              onCreateConversation={createConversation}
              onSelectConversation={openConversation}
              onDeleteConversation={setDeleteTarget}
            />
          ) : (
            <EmoviDocChat
              patientId={patientId}
              conversationId={conversationId}
              selectedConversation={selectedConversation}
              onBack={handleBack}
              onDeleteRequest={setDeleteTarget}
              onConversationUpdated={handleConversationUpdated}
              onReloadConversations={loadConversations}
              onCreateConversation={createConversation}
              isCreatingConversation={isCreatingConversation}
              isDeletingConversation={isDeletingConversation}
            />
          )}
        </div>

        <EmoviDocDisclaimer />
      </SheetContent>

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent size="sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Elimina conversazione</AlertDialogTitle>
            <AlertDialogDescription>
              Questa azione è irreversibile. La conversazione e tutti i messaggi verranno eliminati.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (deleteTarget !== null) {
                  void deleteConversation(deleteTarget);
                  setDeleteTarget(null);
                }
              }}
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  );
}
